/**
 * Controls generated from `ParamSpec` (SPEC §9.4).
 *
 * Three behaviours are specified and all three are here:
 *
 * * **Debounce 400ms**, or an explicit **Run** button when `cache_ttl_seconds`
 *   is 0 — an uncached report is expensive by definition, and firing one on
 *   every keystroke is how you take down a warehouse.
 * * **Cascading params refetch dependents on change and clear invalid
 *   selections.** A city that does not belong to the newly chosen region must
 *   disappear, not linger as an invisible filter.
 * * **Inline field errors for 422**, keyed by the `fields` map in the §10
 *   envelope.
 *
 * The bar owns no state of record. Values live in the URL (§9.1) and arrive as
 * props; every edit is reported upward. That is what makes a link shareable —
 * there is nowhere else for a filter to hide.
 */

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../lib/api'
import { dependencyValues, reconcileWithOptions, renderScalar } from '../lib/params'
import type { DateRange, ParamValue, ParamValues } from '../lib/params'
import type { DatasetResult, Option, ParamSpec } from '../lib/types'

const DEBOUNCE_MS = 400

/** Every dataset in the latest run, keyed as the payload keys them (§8.6). */
type Datasets = Readonly<Record<string, DatasetResult>>

/**
 * A snapshot-derived picker's choices, read out of the run payload (§8.9).
 *
 * The columns are read **positionally**, as every dataset's rows are: startup
 * refuses an `options_dataset` whose columns are not exactly `value` then
 * `label`, so the two indices below are guaranteed rather than hoped for.
 *
 * A row with a null value is dropped. It would render as a choice that filters
 * to nothing, and a picker entry that returns no rows is indistinguishable from
 * a broken report.
 */
function optionsFromDataset(dataset: DatasetResult | undefined): readonly Option[] {
  if (dataset === undefined) return []
  const options: Option[] = []
  for (const row of dataset.rows) {
    const [value, label] = row
    if (value === null || value === undefined || typeof value === 'boolean') continue
    options.push({
      value,
      label: label === null || label === undefined ? String(value) : String(label),
    })
  }
  return options
}

export interface ParamBarProps {
  readonly slug: string
  readonly specs: readonly ParamSpec[]
  readonly values: ParamValues
  /** Per-field messages from a 422 (§10). */
  readonly fieldErrors: Readonly<Record<string, string>>
  /** 0 means an explicit Run button instead of a debounce (§9.4). */
  readonly cacheTtlSeconds: number
  /**
   * The latest run's datasets, which is where an `options_dataset` picker gets
   * its choices (§8.9). Undefined before the first run — a picker filled this
   * way then reads as pending rather than as having no choices.
   */
  readonly datasets?: Datasets | undefined
  readonly onChange: (values: ParamValues) => void
  readonly onRun: () => void
  readonly isRunning: boolean
  /**
   * Rendered right-aligned in the bar — the freshness badge and its refresh
   * control (§9.3). Folding it in here, rather than the report header, is the
   * reference implementation's one-bar layout: filters on the left, "is this
   * current?" on the right. The bar renders even with no parameters when this
   * is present, so a parameterless report still shows its freshness.
   */
  readonly freshness?: ReactNode
}

const labelClass = 'block text-[length:var(--text-xs)] font-medium text-[var(--color-ink-muted)]'
const fieldClass =
  'w-full rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] px-2 py-1.5 text-[length:var(--text-sm)] text-[var(--color-ink)] focus:border-[var(--accent)]'

/**
 * Options for one dynamic picker, refetched whenever a dependency changes.
 *
 * Failure is surfaced, never swallowed into an empty list: an empty picker and
 * a broken picker look identical to a user, and only one of them is their
 * fault.
 */
function useOptions(
  slug: string,
  spec: ParamSpec,
  specs: readonly ParamSpec[],
  values: ParamValues,
  datasets: Datasets,
): { options: readonly Option[]; error: string | null; loading: boolean } {
  const [options, setOptions] = useState<readonly Option[]>(spec.options ?? [])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Serialised so the effect re-runs on a value change, not on every render.
  const dependencies = JSON.stringify(dependencyValues(spec, specs, values))

  useEffect(() => {
    if (spec.options_query === null) return
    let live = true
    setLoading(true)
    api
      .options(slug, spec.name, JSON.parse(dependencies) as Record<string, unknown>)
      .then((loaded) => {
        if (!live) return
        setOptions(loaded)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!live) return
        setOptions([])
        setError(cause instanceof Error ? cause.message : String(cause))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [slug, spec.name, spec.options_query, dependencies])

  if (spec.options_dataset !== null) {
    // §8.9: the choices came with the numbers. Nothing is fetched — an
    // `options_query` here would rescan the view the snapshot exists to avoid
    // scanning, and the endpoint 404s such a parameter for that reason.
    const dataset = datasets[spec.options_dataset]
    return {
      options: optionsFromDataset(dataset),
      error: null,
      // Before the first run there is no payload yet. That is pending, not
      // empty: "No choices available" would be a false statement about the data.
      loading: dataset === undefined,
    }
  }

  return { options, error, loading }
}

function PickerField({
  slug,
  spec,
  specs,
  values,
  datasets,
  onChange,
}: {
  slug: string
  spec: ParamSpec
  specs: readonly ParamSpec[]
  values: ParamValues
  datasets: Datasets
  onChange: (value: ParamValue) => void
}) {
  const { options, error, loading } = useOptions(slug, spec, specs, values, datasets)
  const value = values[spec.name] ?? null

  // When the option list changes under a selection — a cascading refetch, or a
  // shared URL landing on an integer-keyed picker — reconcile once and report
  // the corrected value upward. §9.4: clear invalid selections.
  const reconciled = reconcileWithOptions(spec, value, options)
  const reconciledKey = JSON.stringify(reconciled)
  const currentKey = JSON.stringify(value)
  useEffect(() => {
    if (options.length > 0 && reconciledKey !== currentKey) {
      onChange(JSON.parse(reconciledKey) as ParamValue)
    }
    // `onChange` is intentionally left out of the dependency list: the parent
    // rebuilds it every render, and including it would loop.
  }, [reconciledKey, currentKey, options.length])

  if (error !== null) {
    return (
      <p role="alert" className="text-[length:var(--text-xs)] text-[var(--color-negative)]">
        Could not load choices: {error}
      </p>
    )
  }

  if (spec.kind === 'multiselect') {
    const selected = new Set((Array.isArray(reconciled) ? reconciled : []).map(String))
    return (
      <div
        role="group"
        aria-label={spec.label}
        className="flex flex-wrap gap-1.5"
        data-loading={loading}
      >
        {options.map((option) => {
          const isOn = selected.has(String(option.value))
          return (
            <button
              key={String(option.value)}
              type="button"
              aria-pressed={isOn}
              onClick={() => {
                const next = options
                  .filter((candidate) =>
                    String(candidate.value) === String(option.value)
                      ? !isOn
                      : selected.has(String(candidate.value)),
                  )
                  .map((candidate) => candidate.value)
                onChange(next)
              }}
              className={`rounded-[var(--radius-md)] border px-2 py-1 text-[length:var(--text-xs)] ${
                isOn
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--color-ink)]'
                  : 'border-[var(--color-rule)] text-[var(--color-ink-muted)]'
              }`}
            >
              {option.label}
            </button>
          )
        })}
        {options.length === 0 && !loading && (
          <span className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
            No choices available
          </span>
        )}
      </div>
    )
  }

  return (
    <select
      id={`param-${spec.name}`}
      className={fieldClass}
      value={renderScalar(reconciled) ?? ''}
      disabled={loading && options.length === 0}
      onChange={(event) => {
        const picked = options.find((option) => String(option.value) === event.target.value)
        onChange(picked?.value ?? null)
      }}
    >
      {(reconciled === null || !spec.required) && <option value="">Any</option>}
      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

function Field({
  slug,
  spec,
  specs,
  values,
  datasets,
  error,
  onChange,
}: {
  slug: string
  spec: ParamSpec
  specs: readonly ParamSpec[]
  values: ParamValues
  datasets: Datasets
  error: string | undefined
  onChange: (value: ParamValue) => void
}) {
  const value = values[spec.name] ?? null

  let control
  switch (spec.kind) {
    case 'select':
    case 'multiselect':
      control = (
        <PickerField
          slug={slug}
          spec={spec}
          specs={specs}
          values={values}
          datasets={datasets}
          onChange={onChange}
        />
      )
      break
    case 'date_range': {
      const range = (value ?? { from: '', to: '' }) as DateRange
      control = (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            aria-label={`${spec.label} from`}
            className={`${fieldClass} numeric`}
            value={range.from}
            onChange={(event) => {
              onChange({ from: event.target.value, to: range.to })
            }}
          />
          <span aria-hidden className="text-[var(--color-ink-muted)]">
            –
          </span>
          <input
            type="date"
            aria-label={`${spec.label} to`}
            className={`${fieldClass} numeric`}
            value={range.to}
            onChange={(event) => {
              onChange({ from: range.from, to: event.target.value })
            }}
          />
        </div>
      )
      break
    }
    case 'date':
      control = (
        <input
          id={`param-${spec.name}`}
          type="date"
          className={`${fieldClass} numeric`}
          value={renderScalar(value) ?? ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : event.target.value)
          }}
        />
      )
      break
    case 'number':
      control = (
        <input
          id={`param-${spec.name}`}
          type="number"
          className={`${fieldClass} numeric`}
          value={renderScalar(value) ?? ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : Number(event.target.value))
          }}
        />
      )
      break
    case 'boolean':
      control = (
        <label className="flex items-center gap-2 text-[length:var(--text-sm)]">
          <input
            id={`param-${spec.name}`}
            type="checkbox"
            checked={value === true}
            onChange={(event) => {
              onChange(event.target.checked)
            }}
          />
          {spec.label}
        </label>
      )
      break
    default:
      control = (
        <input
          id={`param-${spec.name}`}
          type="text"
          className={fieldClass}
          value={renderScalar(value) ?? ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : event.target.value)
          }}
        />
      )
  }

  // A set of toggle buttons (the Excel-slicer style) takes a whole row of its
  // own, label on the left, so a long list wraps across the page instead of
  // stacking one or two buttons per line in a narrow column.
  if (spec.kind === 'multiselect') {
    return (
      <div className="flex basis-full flex-wrap items-start gap-x-3 gap-y-1">
        <span className={`${labelClass} w-28 shrink-0 pt-1.5`} id={`param-${spec.name}-label`}>
          {spec.label}
        </span>
        <div className="min-w-0 flex-1">
          {control}
          {error !== undefined && (
            <p
              role="alert"
              className="mt-1 text-[length:var(--text-xs)] text-[var(--color-negative)]"
              id={`param-${spec.name}-error`}
            >
              {error}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-48 flex-1">
      {spec.kind !== 'boolean' && (
        <label className={labelClass} htmlFor={`param-${spec.name}`}>
          {spec.label}
          {spec.required && (
            <span aria-hidden className="ml-0.5 text-[var(--color-ink-muted)]">
              *
            </span>
          )}
        </label>
      )}
      <div className="mt-1">{control}</div>
      {error !== undefined && (
        <p
          role="alert"
          className="mt-1 text-[length:var(--text-xs)] text-[var(--color-negative)]"
          id={`param-${spec.name}-error`}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export function ParamBar({
  slug,
  specs,
  values,
  fieldErrors,
  cacheTtlSeconds,
  datasets = {},
  onChange,
  onRun,
  isRunning,
  freshness,
}: ParamBarProps) {
  // An uncached report runs only when asked (§9.4).
  const manualRun = cacheTtlSeconds === 0
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const change = (name: string, value: ParamValue): void => {
    const next = { ...values, [name]: value }
    onChange(next)
    if (manualRun) return
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(onRun, DEBOUNCE_MS)
  }

  // Nothing to show only when there are no controls, no Run button, and no
  // freshness slot — otherwise the bar still carries "is this current?".
  if (specs.length === 0 && !manualRun && freshness === undefined) return null

  // Toggle-button filters first, one row each; the dropdowns and dates share
  // the row beneath them. A bar with button rows is too tall to pin to the top
  // of the screen -- it would cover the report as the reader scrolls.
  const buttons = specs.filter((spec) => spec.kind === 'multiselect')
  const ordered = [...buttons, ...specs.filter((spec) => spec.kind !== 'multiselect')]
  const pinned = buttons.length === 0 ? 'sticky top-0 z-20 ' : ''

  return (
    <div
      className={`${pinned}flex flex-wrap items-end gap-4 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-3`}
    >
      {ordered.map((spec) => (
        <Field
          key={spec.name}
          slug={slug}
          spec={spec}
          specs={specs}
          values={values}
          datasets={datasets}
          error={fieldErrors[spec.name]}
          onChange={(value) => {
            change(spec.name, value)
          }}
        />
      ))}
      {manualRun && (
        <button
          type="button"
          onClick={onRun}
          disabled={isRunning}
          className="rounded-[var(--radius-md)] bg-[var(--accent)] px-3 py-1.5 text-[length:var(--text-sm)] font-medium text-white disabled:opacity-60"
        >
          {isRunning ? 'Running…' : 'Run'}
        </button>
      )}
      {freshness !== undefined && (
        // `ml-auto` pushes freshness to the right of the bar, as the reference's
        // filter-spacer does; it wraps below the controls on a narrow viewport.
        <div className="ml-auto flex items-end">{freshness}</div>
      )}
    </div>
  )
}
